import { getOr } from '@/utils/getOr';

interface FormatNumberOptions {
  /** Add before the number */
  prepend?: string;
  /** Add after the number */
  append?: string;
  /** Specify the decimals */
  decimals?: number;
  /**
   * Skip any last decimals if they are 0.
   * e.g: 1.00 => 1, 1.10 => 1.1
   */
  trimZeroDecimals?: boolean;
  /** Specify what to do in the case of a NaN */
  nan?: string;
  /**
   * Displays the sign after the prepend
   * e.g: `-$100` => `$-100`
   */
  signAfterPrepend?: boolean;
  /**
   * Displays the sign, both positive and negative
   * e.g: `$100` => `+$100`
   */
  showSign?: boolean;
}

export const formatNumber = (value: number | string, options?: FormatNumberOptions) => {
  const nan = getNumberOptionValue(options, 'nan', '');
  const decimals = getNumberOptionValue(options, 'decimals', 0);
  const prepend = getNumberOptionValue(options, 'prepend', '');
  const append = getNumberOptionValue(options, 'append', '');
  const trimZeroDecimals = getNumberOptionValue(options, 'trimZeroDecimals', false);
  const signAfterPrepend = getNumberOptionValue(options, 'signAfterPrepend', false);
  const showSign = getNumberOptionValue(options, 'showSign', false);

  const valueParsed = Number(value);

  if (`${value}` === 'NaN' || isNaN(valueParsed)) {
    return nan;
  }

  const unsignedValue = Math.abs(valueParsed);
  let sign = valueParsed >= 0 ? '+' : '-';

  // Hide the `+` sign if showSign is disabled
  if (sign === '+' && !showSign) {
    sign = '';
  }

  const resultParts: string[] = [];

  // Order of the sign and prepend
  if (!signAfterPrepend) {
    // Add the sign before the prepend
    resultParts.push(sign, prepend);
  } else {
    // Add the sign after the prepend
    resultParts.push(prepend, sign);
  }

  // Add the rest of the number
  resultParts.push(
    (+`${unsignedValue}`).toLocaleString('en-US', {
      maximumFractionDigits: decimals,
      minimumFractionDigits: decimals,
    }),
    append,
  );

  let result = resultParts.join('');

  if (decimals !== 0 && trimZeroDecimals) {
    // Remove any last decimals
    result = result.replace(/0*$/, '');
    // Remove the `.` character in the case of 1.00 => 1.
    result = result.replace(/\.$/, '');
  }

  return result;
};

const getNumberOptionValue = <T extends keyof FormatNumberOptions, R extends NoUndefined<FormatNumberOptions[T]>>(
  options: FormatNumberOptions | undefined,
  key: T,
  defaultValue: R,
): NoUndefined<FormatNumberOptions[T]> =>
  options ? getOr<R, FormatNumberOptions, string>(options, key, defaultValue) : (defaultValue ?? defaultValue);
